import { useCallback } from 'react';
import { logError } from 'utils/logger';
import { MutationResponse } from 'global';
import { useMutation } from '@apollo/client';
import { useNotifications } from 'modules/notification';
import { MutationHookOptions } from '@apollo/client/react/types/types';

import { dealSheetConfigurationApi } from './index';
import { DealSheetAPINameSpase } from '../../types';

export function useChargesConfiguration() {
  const { showNotification } = useNotifications();

  const [onCreate, { loading: isConfigurationCreating }] = useMutation<DealSheetAPINameSpase<MutationResponse<any>>>(
    dealSheetConfigurationApi.createConfig
  );

  const handleCreateConfiguration = useCallback(
    async (
      dto: Record<string, any>,
      fetchOptions?: (api: typeof dealSheetConfigurationApi) => Omit<MutationHookOptions, 'variables'>
    ) => {
      try {
        const { data } = await onCreate({
          variables: { input: dto },
          ...(typeof fetchOptions === 'function' ? fetchOptions(dealSheetConfigurationApi) : {}),
        });

        if (data?.dealSheet?.result?.error || !data?.dealSheet?.result?.success) {
          showNotification(data?.dealSheet?.result?.error ?? 'Something went wrong!', { type: 'error' });
          logError(data?.dealSheet?.result?.error);
          return null;
        }

        showNotification('Configuration created!');

        return data.dealSheet.result!.entity;
      } catch (error: any) {
        logError(error);
        showNotification(error?.message, { type: 'error' });
      }
    },
    [onCreate, showNotification]
  );

  const [onUpdate, { loading: isConfigurationUpdating }] = useMutation<DealSheetAPINameSpase<MutationResponse<any>>>(
    dealSheetConfigurationApi.updateConfig
  );

  const handleUpdateConfiguration = useCallback(
    async (
      dto: Record<string, any>,
      fetchOptions?: (api: typeof dealSheetConfigurationApi) => Omit<MutationHookOptions, 'variables'>
    ) => {
      try {
        const { data } = await onUpdate({
          variables: { input: dto },
          ...(typeof fetchOptions === 'function' ? fetchOptions(dealSheetConfigurationApi) : {}),
        });

        if (data?.dealSheet?.result?.error || !data?.dealSheet?.result?.success) {
          showNotification(data?.dealSheet?.result?.error ?? 'Something went wrong!', { type: 'error' });
          logError(data?.dealSheet?.result?.error);
          return null;
        }

        showNotification('Configuration updated!');

        return data.dealSheet.result!.entity;
      } catch (error: any) {
        logError(error);
        showNotification(error?.message, { type: 'error' });
      }
    },
    [onUpdate, showNotification]
  );

  const [onDelete, { loading: isConfigurationDeleting }] = useMutation<DealSheetAPINameSpase<MutationResponse<any>>>(
    dealSheetConfigurationApi.deleteConfig
  );

  const handleDeleteConfiguration = useCallback(
    async (
      { id }: { id: string },
      fetchOptions?: (api: typeof dealSheetConfigurationApi) => Omit<MutationHookOptions, 'variables'>
    ) => {
      try {
        const { data } = await onDelete({
          variables: { input: { id } },
          ...(typeof fetchOptions === 'function' ? fetchOptions(dealSheetConfigurationApi) : {}),
        });

        if (data?.dealSheet?.result?.error || !data?.dealSheet?.result?.success) {
          showNotification(data?.dealSheet?.result?.error ?? 'Something went wrong!', { type: 'error' });
          logError(data?.dealSheet?.result?.error);
          return null;
        }

        showNotification('Configuration deleted!');

        return data.dealSheet.result!.entity;
      } catch (error: any) {
        logError(error);
        showNotification(error?.message, { type: 'error' });
      }
    },
    [onDelete, showNotification]
  );

  return {
    isConfigurationCreating,
    isConfigurationUpdating,
    isConfigurationDeleting,
    handleCreateConfiguration,
    handleUpdateConfiguration,
    handleDeleteConfiguration,
  };
}
